<!--
Market checklist for a meal plan: fetches the server-side aggregation (computed on
read) and renders it as a check-off list. Checked state is device-local, persisted
to localStorage exactly like the per-recipe ingredient tracker in Recipe_internal.vue.

<MealPlanChecklist :plan_id="plan.id" />
-->
<template>
    <div class="checklist">
        <div class="checklist_header">
            <h2>{{ $t('meal_plans.checklist.title') }}</h2>
            <button type="button" class="clear_link" v-if="checked.length > 0" @click="clearChecked()">{{ $t('meal_plans.checklist.clear') }}</button>
        </div>
        <p class="error_text" v-if="error">{{ error }}</p>
        <p class="empty_text" v-if="!error && loaded && lines.length < 1 && unparseable.length < 1">{{ $t('meal_plans.checklist.empty') }}</p>

        <ul class="items">
            <li v-for="line in lines" :key="itemKey(line)">
                <label :class="{ done: checked.includes(itemKey(line)) }">
                    <input type="checkbox" :value="itemKey(line)" v-model="checked" @change="saveChecked()" />
                    <span class="item_quantity">{{ line.quantity }} {{ line.unit }}</span>
                    <span class="item_name">{{ line.name }}</span>
                </label>
            </li>
        </ul>

        <div v-if="unparseable.length > 0" class="unparseable_section">
            <h3>{{ $t('meal_plans.checklist.unparseable') }}</h3>
            <ul class="items">
                <li v-for="(item, index) in unparseable" :key="'u_' + index">
                    <label :class="{ done: checked.includes(unparseableKey(item, index)) }">
                        <input type="checkbox" :value="unparseableKey(item, index)" v-model="checked" @change="saveChecked()" />
                        <span class="item_quantity">{{ item.quantity }} {{ item.unit }}</span>
                        <span class="item_name">{{ item.name }}</span>
                        <span class="item_source">({{ item.recipe_id }})</span>
                    </label>
                </li>
            </ul>
        </div>

        <p class="missing_note" v-if="missing_recipes.length > 0">
            {{ $t('meal_plans.checklist.missing') }}: {{ missing_recipes.join(', ') }}
        </p>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "MealPlanChecklist",
    props: [
        'plan_id'
    ],
    data() {
        return {
            lines: [],
            unparseable: [],
            missing_recipes: [],
            checked: [],
            loaded: false,
            error: null
        }
    },
    computed: {
        ...mapGetters({
            user: "user"
        })
    },
    mounted() {
        this.loadChecked();
        this.getChecklist();
    },
    methods: {
        getChecklist() {
            fetch(process.env.VUE_APP_API + `/mealplans/` + this.plan_id + `/checklist`, {
                headers: {
                    "TOKEN": JSON.stringify(this.user.data.token)
                }
            }).then(res => res.json())
              .then(res => {
                  this.loaded = true;
                  if (res.error) {
                      this.error = res.error;
                  } else {
                      this.lines = res.checklist.lines;
                      this.unparseable = res.checklist.unparseable;
                      this.missing_recipes = res.checklist.missing_recipes;
                  }
              });
        },
        // Keyed by content, not index, so checkmarks survive re-fetches and reordering.
        itemKey(line) {
            return line.name + '|||' + line.unit;
        },
        unparseableKey(item, index) {
            return 'u|||' + item.recipe_id + '|||' + item.name + '|||' + index;
        },
        saveChecked() {
            localStorage.setItem(`syntages_mealplan_${this.plan_id}_checklist`, JSON.stringify(this.checked));
        },
        loadChecked() {
            try {
                this.checked = JSON.parse(localStorage.getItem(`syntages_mealplan_${this.plan_id}_checklist`)) || [];
            } catch (e) {
                this.checked = [];
            }
        },
        clearChecked() {
            this.checked.splice(0, this.checked.length);
            this.saveChecked();
        }
    }
}
</script>

<style scoped>
.checklist {
    text-align: left;
}
.checklist_header {
    display: flex;
    align-items: baseline;
    gap: 1rem;
}
.checklist_header h2 {
    font-size: 1.3rem;
}
.clear_link {
    background: none;
    border: none;
    color: var(--color-grey-2);
    font-size: 0.85rem;
    cursor: pointer;
    text-decoration: underline;
}
.clear_link:hover {
    color: var(--color-secondary);
}
.items {
    list-style: none;
    padding: 0;
}
.items li {
    padding: 0.3rem 0;
    border-bottom: var(--line-dashed);
}
.items label {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    cursor: pointer;
    margin: 0;
}
.items label.done .item_name,
.items label.done .item_quantity {
    text-decoration: line-through;
    color: var(--color-grey-2);
}
.item_quantity {
    min-width: 90px;
    color: var(--color-grey-2);
    font-size: 0.9rem;
}
.unparseable_section {
    margin-top: 1.5rem;
}
.unparseable_section h3 {
    font-size: 1rem;
    color: var(--color-grey-2);
}
.item_source {
    color: var(--color-grey-2);
    font-size: 0.8rem;
}
.missing_note, .empty_text, .error_text {
    color: var(--color-grey-2);
    margin-top: 1rem;
}
</style>
